import Logger from "@cappelendamm/logger";
import { getImageUrl } from "../crystallize/utils/images.js";
import { getHierarchiesFromTopics } from "./hierarchy.js";
import { filterDuplicates, filterNullArrays } from "@cappelendamm/utils/array";
import { getImmediacyScore } from "./get-immediacy-score.js";
import { subMonths } from "date-fns";
import { Meilisearch } from "meilisearch";
import { indexAuthorByScids } from "./author-index.js";
/**
 * Creates a receiver (Message Endpoint) that indexes products in MeiliSearch.
 * In Enterprise Integration Patterns, this is a Message Endpoint that receives normalized product data.
 * @see https://www.enterpriseintegrationpatterns.com/patterns/messaging/MessageEndpoint.html
 */
export function searchIndexReceiver({ masterKey, host }, crystallize) {
    const meilisearchClient = new Meilisearch({ apiKey: masterKey, host });
    async function searchIndexReceiver(items) {
        // Index to items index
        const index = meilisearchClient.index("items");
        const { updates, removals } = getIndexOperations(items);
        const subscriptionVariantsIndex = meilisearchClient.index("subscription-variants");
        const subscriptionVariantOperations = getSubscriptionVariantIndexOperations(items);
        const [itemUpdateTasks, subscriptionVariantUpdateTasks] = await Promise.all([
            index.addDocumentsInBatches(updates, 1000, { primaryKey: "objectID" }),
            subscriptionVariantsIndex.addDocumentsInBatches(subscriptionVariantOperations.updates, 1000, { primaryKey: "objectID" }),
        ]);
        await Promise.all([
            ...itemUpdateTasks,
            index.deleteDocuments(removals),
            ...subscriptionVariantUpdateTasks,
            subscriptionVariantsIndex.deleteDocuments(subscriptionVariantOperations.removals),
        ]);
        // Index to Author index
        const scids = filterDuplicates([
            ...items
                .filter((item) => item?.itemType === "book")
                .flatMap((book) => book.itemDetails.authors.map((author) => author.scid)),
            ...items
                .filter((item) => item?.itemType === "contributor")
                .map((contributor) => contributor.scid),
        ]).map(String);
        await indexAuthorByScids({ scids }, { meilisearch: { masterKey, host }, crystallize });
        Logger.log(`Meilisearch\n${updates.length} documents updated\n${removals.length} removed`);
    }
    return searchIndexReceiver;
}
const INDEXED_ITEMS = [
    "book",
    "product-bundle",
    "subscription",
    "digital-service",
];
export function isProductVariant(item) {
    return item.sku !== undefined;
}
export function isContributor(item) {
    return item.scid !== undefined;
}
export function isLiteraryWork(item) {
    return item.itemType === "literary-work";
}
export function isSeries(item) {
    return item.itemType === "series";
}
export function isIndexedProductDocument(variant) {
    return INDEXED_ITEMS.some((itemType) => itemType === variant.itemType);
}
function isVisibleProduct(variant) {
    if (variant.itemType === "digital-service")
        return true;
    if (!variant.canBeVisible)
        return false;
    if (variant.itemType === "subscription" && !variant.isDefaultVariant)
        return false;
    return true;
}
export function getIndexOperations(items) {
    const productRemovals = items
        .filter(isProductVariant)
        .filter((v) => !isVisibleProduct(v))
        .map((v) => v.sku);
    const productUpdates = items
        .filter(isProductVariant)
        .filter(isIndexedProductDocument)
        .filter(isVisibleProduct)
        .map(mapProductVariantToDocument);
    const contributorUpdates = items
        .filter(isContributor)
        .map(mapContributorToDocument);
    const literaryWorkUpdates = items
        .filter(isLiteraryWork)
        .map(mapLiteraryWorkToDocument);
    const seriesUpdates = items
        .filter(isSeries)
        .map(mapSeriesToDocument);
    const updates = [
        ...productUpdates,
        ...contributorUpdates,
        ...literaryWorkUpdates,
        ...seriesUpdates,
    ];
    return { updates, removals: productRemovals };
}
export function getSubscriptionVariantIndexOperations(items) {
    const productVariants = items.filter(isProductVariant);
    const subscriptions = productVariants.filter((variant) => variant.itemType === "subscription");
    return {
        updates: subscriptions.map((subscription) => mapProductVariantToDocument(subscription)),
        removals: productVariants
            .filter((variant) => variant.itemType !== "subscription")
            .map((variant) => variant.sku),
    };
}
export function mapProductVariantToDocument(item) {
    const hierarchies = getHierarchiesFromTopics((item.topics ?? []).map((t) => t?.pathLabel ?? null));
    const publicationDate = item.itemDetails.publicationDate ?? null;
    const publicationTimestamp = publicationDate
        ? Date.parse(publicationDate) || null
        : null;
    const bookFields = item.itemType === "book"
        ? {
            authors: filterNullArrays(item.itemDetails.authors.map((c) => c?.name)),
            illustrators: filterNullArrays(item.itemDetails.illustrators.map((c) => c?.name)),
            narrators: filterNullArrays(item.itemDetails.narrators.map((c) => c?.name)),
            translators: filterNullArrays(item.itemDetails.translators.map((c) => c?.name)),
            format: item.itemDetails.binding,
            evaluationCopyType: item.itemDetails.evaluationCopyType,
            language: item.itemDetails.language,
            literaryWorkName: item.itemDetails.literaryWork?.name,
        }
        : null;
    const digitalServicesFields = item.itemType === "digital-service"
        ? {
            literaryWorkName: item.itemDetails.literaryWork?.name,
        }
        : null;
    const subscriptionFields = item.itemType === "subscription"
        ? {
            name: item.itemDetails.name,
            imageUrl: item.itemDetails.image?.url,
        }
        : null;
    return {
        ...item,
        objectID: item.sku,
        isbn: item.sku,
        imageUrl: getImageUrl(item.images, 700),
        price: item.price ?? null,
        subjectToVAT: item.subjectToVAT ?? null,
        publicationDate,
        publicationTimestamp,
        slug: item.itemDetails.slug,
        descriptionHtml: item.itemDetails.description?.html,
        description: item.itemDetails.description?.plainText?.join(" "),
        immediacyScore: getImmediacyScore({
            publicationTimestamp,
            objectID: item.sku,
            canBeSold: item.canBeSold,
            isDefaultVariant: item.isDefaultVariant,
            statusCode: item.statusCode ?? null,
        }),
        ...bookFields,
        ...hierarchies,
        ...subscriptionFields,
        ...digitalServicesFields,
    };
}
export function mapContributorToDocument(item) {
    const hierarchies = getHierarchiesFromTopics((item.topics ?? []).map((t) => t?.pathLabel ?? null));
    const updatedAt = item.updatedAt ?? null;
    const updatedAtTimestamp = updatedAt
        ? subMonths(updatedAt, 6).valueOf() || null
        : null;
    return {
        ...item,
        objectID: String(item.scid),
        imageUrl: getImageUrl(item.images, 700),
        updatedAt,
        slug: item.slug,
        publicationTimestamp: updatedAtTimestamp,
        biographyHtml: item.itemDetails.biography?.html,
        description: item.itemDetails.biography?.plainText?.join(" "),
        immediacyScore: getImmediacyScore({
            publicationTimestamp: updatedAtTimestamp,
            objectID: String(item.scid),
            canBeSold: !!item.images && item.images.length > 0,
            isDefaultVariant: true,
            statusCode: null,
        }),
        ...hierarchies,
    };
}
export function mapLiteraryWorkToDocument(item) {
    const hierarchies = getHierarchiesFromTopics((item.topics ?? []).map((t) => t?.pathLabel ?? null));
    const updatedAt = item.updatedAt ?? null;
    const updatedAtTimestamp = updatedAt
        ? subMonths(updatedAt, 6).valueOf() || null
        : null;
    const objectID = `literary-work-${item.literaryWorkId}`;
    return {
        ...item,
        ...hierarchies,
        objectID,
        imageUrl: getImageUrl(item.images, 700),
        updatedAt,
        slug: item.slug,
        publicationTimestamp: updatedAtTimestamp,
        immediacyScore: getImmediacyScore({
            publicationTimestamp: updatedAtTimestamp,
            objectID,
            canBeSold: !!item.images && item.images.length > 0,
            isDefaultVariant: true,
            statusCode: null,
        }),
    };
}
export function mapSeriesToDocument(item) {
    const hierarchies = getHierarchiesFromTopics((item.topics ?? []).map((t) => t?.pathLabel ?? null));
    const updatedAt = item.updatedAt ?? null;
    const updatedAtTimestamp = updatedAt
        ? subMonths(updatedAt, 6).valueOf() || null
        : null;
    const objectID = `series-${item._id}`;
    return {
        ...item,
        objectID,
        ...hierarchies,
        imageUrl: getImageUrl(item.images, 700),
        updatedAt,
        slug: item.slug,
        publicationTimestamp: updatedAtTimestamp,
        immediacyScore: getImmediacyScore({
            publicationTimestamp: updatedAtTimestamp,
            objectID,
            canBeSold: !!item.images && item.images.length > 0,
            isDefaultVariant: true,
            statusCode: null,
        }),
    };
}
